import React, {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import {
  Formik,
  FormikHelpers,
  FormikValues,
  getIn,
} from 'formik';
import {
  Alert,
  Button,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import Swal from 'sweetalert2';
import axios from 'axios';
import { SellerContact, SellerContactPreference } from '../../../structures/models/seller';
import { addPhoneFormatting, displayPhoneNumber, getPhonePlaceholder } from '../../../utils/formatters';
import RequiredFieldLabel from '../Partials/RequiredFieldLabel';
import ToggleInput from '../Partials/ToggleInput';
import routes from '../../../utils/routes';
import SellerContactValidation from './validation';

type Props = {
  contact: SellerContact,
  onSave: (
    contact: SellerContact,
    formikHelpers: FormikHelpers<SellerContact>,
    setFormError: (message: string) => void,
    setSubmitting: (submitting: boolean) => void,
  ) => void,
  onCancel: () => void,
  useScroll?: boolean,
  isModal?: boolean,
};

const EditSellerContact: React.FC<Props> = ({
  contact,
  onSave,
  onCancel,
  useScroll,
  isModal = false,
}) => {
  const [contactData, setContactData] = useState<SellerContact>(contact);
  const [contactDataLoaded, setContactDataLoaded] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const formRef = useRef(null);

  const formatSellerContactData = (values: SellerContact): SellerContact => ({
    ...values,
    phone: values.phone ? displayPhoneNumber(values.phone) : '',
    alternate_phone: values.alternate_phone ? displayPhoneNumber(values.alternate_phone) : '',
  });

  const fetchSellerContact = async (id: number): Promise<SellerContact> => {
    return axios.get(routes.apiSellerGet(contact.seller_id))
      .then((response) => {
        const { data } = response;
        return data.data.contacts.find((c: SellerContact) => c.id === id);
      })
      .catch((error) => {
        throw new Error(error);
      });
  };

  // If the contact that is being edited changes, re-initialize the form
  useEffect(() => {
    setFormError('');
    setContactData(contact);
    setContactDataLoaded(false);
    if (contact.id) {
      fetchSellerContact(contact.id)
        .then((data) => {
          if (data) {
            setContactData(formatSellerContactData(data));
            setContactDataLoaded(true);
          } else {
            Swal.fire({
              title: 'Not Found',
              text: 'The contact was not found. Please try again.',
              icon: 'info',
              confirmButtonText: 'Ok',
            }).then(() => window.location.reload());
          }
        })
        .catch((error) => {
          Swal.fire({
            title: 'Error',
            text: 'There was an error loading the contact. Please try again.',
            icon: 'error',
            confirmButtonText: 'Ok',
          }).then(() => window.location.reload());
        });
    } else {
      setContactDataLoaded(true);
    }
  }, [contact]);

  useEffect(() => {
    if (useScroll) {
      if (formRef) {
        const { current } = formRef as MutableRefObject<any>;
        if (current) {
          current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, []);

  // Helper for saving contact preference when it's value changes
  const setContactPreference = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    values: FormikValues,
    preference: SellerContactPreference,
    checked: boolean,
    isAlternate?: boolean,
  ): void => {
    const preferencesField = isAlternate ? 'contact_preference_alternate' : 'contact_preference';
    const preferences: SellerContactPreference[] = getIn(values, preferencesField);
    const existingIndex = preferences.indexOf(preference);

    if (checked) {
      if (existingIndex === -1) {
        setFieldValue(preferencesField, [...preferences, preference]);
      }
    } else if (existingIndex !== -1) {
      // Duplicate to avoid mutating original array with splice
      const duplicatePreferences = preferences.slice();
      duplicatePreferences.splice(existingIndex, 1);
      setFieldValue(preferencesField, duplicatePreferences);
    }
  };

  // Simple helper function
  const isPhoneRequired = (values: SellerContact): boolean => {
    return values.contact_preference.includes('call')
      || values.contact_preference.includes('text')
      || (values.alternate_phone !== null && values.alternate_phone.length > 0);
  };

  return (
    <Formik
      initialValues={contactData}
      onSubmit={
        (values, formikHelpers) => onSave(values, formikHelpers, setFormError, setSubmitting)
      }
      validationSchema={SellerContactValidation}
      validateOnBlur
      validateOnMount
      validateOnChange
      // Reinitializes the form when the initial values change (the `contact` prop)
      enableReinitialize
    >
      {
        ({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          submitForm,
          setFieldValue,
        }) => {
          return (
            <div
              ref={formRef}
              className={`form-edit-seller-contact ${contactDataLoaded ? 'form-ready' : 'form-loading'}`}
            >
              <Row className="mt-3">
                <Col xs={12}>
                  <h2>
                    Editing
                    {' '}
                    {contact.name}
                  </h2>
                </Col>
                {!!formError && (
                  <Col xs={12}>
                    <Alert variant="danger">
                      {formError}
                    </Alert>
                  </Col>
                )}
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="name">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'name') && getIn(errors, 'name')}>
                      <Form.Label className="font-weight-bold small-txt text-muted">Name</Form.Label>
                    </RequiredFieldLabel>
                    <Form.Control
                      type="text"
                      placeholder="Name"
                      value={getIn(values, 'name')}
                      isInvalid={getIn(touched, 'name') && getIn(errors, 'name')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'name')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="contact-email">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'email') && getIn(errors, 'email')}>
                      <Form.Label className="font-weight-bold small-txt text-muted">Email</Form.Label>
                    </RequiredFieldLabel>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={getIn(values, 'email')}
                      isInvalid={getIn(touched, 'email') && getIn(errors, 'email')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'email')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="contact_preference">
                    <RequiredFieldLabel
                      isInvalid={getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference')}
                    >
                      <Form.Label className="font-weight-bold small-txt text-muted">Contact Preference</Form.Label>
                    </RequiredFieldLabel>
                    <div className="d-flex flex-wrap">
                      <ToggleInput
                        id="contact-preference-email"
                        label="Messages / Email"
                        checked={getIn(values, 'contact_preference').includes('email')}
                        onChange={(e) => setContactPreference(setFieldValue, values, 'email', e.target.checked)}
                        onBlur={handleBlur}
                        className="mr-4 mb-4"
                      />
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          (getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference'))
                            ? 'd-block mt-n3'
                            : ''
                        }
                      >
                        {getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference')}
                      </Form.Control.Feedback>
                    </div>
                    {/* Don't display errors here. Laravel returns errors like:
                    { contact_preference.1: '...error'}, which is difficult to parse with react.
                    If there are phantom errors reported by users, maybe this is it */}
                  </Form.Group>
                </Col>
                {(isModal && (
                  <hr className="w-97 m-auto pt-2 pb-2" />
                ))}
                <Col xs={12} md={3} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="phone">
                    {isPhoneRequired(values) ? (
                      <RequiredFieldLabel isInvalid={getIn(touched, 'phone') && getIn(errors, 'phone')}>
                        <Form.Label className="font-weight-bold small-txt text-muted">Phone Number</Form.Label>
                      </RequiredFieldLabel>
                    ) : (
                      <Form.Label className="font-weight-bold small-txt text-muted">Phone Number</Form.Label>
                    )}
                    <Form.Control
                      type="text"
                      placeholder={getPhonePlaceholder()}
                      value={getIn(values, 'phone')}
                      isInvalid={getIn(touched, 'phone') && getIn(errors, 'phone')}
                      onChange={(e) => setFieldValue('phone', addPhoneFormatting(e.target.value))}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'phone')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="phone_extn">
                    <Form.Label className="font-weight-bold small-txt text-muted">Extn</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="1234"
                      value={getIn(values, 'phone_extn') ?? ''}
                      isInvalid={getIn(touched, 'phone_extn') && getIn(errors, 'phone_extn')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'phone_extn')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="contact_preference">
                    <RequiredFieldLabel
                      isInvalid={getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference')}
                    >
                      <Form.Label className="font-weight-bold small-txt text-muted">Contact Preference</Form.Label>
                    </RequiredFieldLabel>
                    <div className="d-flex flex-wrap">
                      <ToggleInput
                        id="contact-preference-call"
                        label="Call"
                        checked={getIn(values, 'contact_preference').includes('call')}
                        onChange={(e) => setContactPreference(setFieldValue, values, 'call', e.target.checked)}
                        onBlur={handleBlur}
                        className="mr-4 mb-4"
                      />
                      <ToggleInput
                        id="contact-preference-text"
                        label="Text"
                        checked={getIn(values, 'contact_preference').includes('text')}
                        onChange={(e) => setContactPreference(setFieldValue, values, 'text', e.target.checked)}
                        onBlur={handleBlur}
                        className="mb-4 mr-4"
                      />
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          (getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference'))
                            ? 'd-block mt-n3'
                            : ''
                        }
                      >
                        {getIn(touched, 'contact_preference') && getIn(errors, 'contact_preference')}
                      </Form.Control.Feedback>
                    </div>
                    {/* Don't display errors here. Laravel returns errors like:
                    { contact_preference.1: '...error'}, which is difficult to parse with react.
                    If there are phantom errors reported by users, maybe this is it */}
                  </Form.Group>
                </Col>
                {(isModal && (
                  <hr className="w-97 m-auto pt-2 pb-2" />
                ))}
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="alternate_phone">
                    <Form.Label className="font-weight-bold small-txt text-muted">Alt. Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={getPhonePlaceholder()}
                      value={getIn(values, 'alternate_phone')}
                      isInvalid={getIn(touched, 'alternate_phone') && getIn(errors, 'alternate_phone')}
                      onChange={(e) => setFieldValue('alternate_phone', addPhoneFormatting(e.target.value))}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'alternate_phone')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="alternate_phone_extn">
                    <Form.Label className="font-weight-bold small-txt text-muted">Alt. Extn</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="1234"
                      value={getIn(values, 'alternate_phone_extn') ?? ''}
                      isInvalid={getIn(touched, 'alternate_phone_extn') && getIn(errors, 'alternate_phone_extn')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'alternate_phone_extn')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="contact_preference">
                    <RequiredFieldLabel
                      isInvalid={getIn(touched, 'contact_preference_alternate') && getIn(errors, 'contact_preference_alternate')}
                    >
                      <Form.Label className="font-weight-bold small-txt text-muted">Contact Preference</Form.Label>
                    </RequiredFieldLabel>
                    <div className="d-flex flex-wrap">
                      <ToggleInput
                        id="contact-preference-alternate-call"
                        label="Call"
                        checked={getIn(values, 'contact_preference_alternate').includes('call')}
                        onChange={(e) => setContactPreference(setFieldValue, values, 'call', e.target.checked, true)}
                        onBlur={handleBlur}
                        className="mr-4 mb-4"
                      />
                      <ToggleInput
                        id="contact-preference-alternate-text"
                        label="Text"
                        checked={getIn(values, 'contact_preference_alternate').includes('text')}
                        onChange={(e) => setContactPreference(setFieldValue, values, 'text', e.target.checked, true)}
                        onBlur={handleBlur}
                        className="mb-4 mr-4"
                      />
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          (getIn(touched, 'contact_preference_alternate') && getIn(errors, 'contact_preference_alternate'))
                            ? 'd-block mt-n3'
                            : ''
                        }
                      >
                        {getIn(touched, 'contact_preference_alternate') && getIn(errors, 'contact_preference_alternate')}
                      </Form.Control.Feedback>
                    </div>
                    {/* Don't display errors here. Laravel returns errors like:
                    { contact_preference.1: '...error'}, which is difficult to parse with react.
                    If there are phantom errors reported by users, maybe this is it */}
                  </Form.Group>
                </Col>
                {(isModal && (
                  <hr className="w-97 m-auto pt-2 pb-2" />
                ))}
                <Col xs={12} md={4} lg={isModal ? 4 : 2}>
                  <Form.Group controlId="best_time_to_call">
                    <Form.Label className="font-weight-bold small-txt text-muted">Best Time To Call</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="After 5pm"
                      value={getIn(values, 'best_time_to_call')}
                      isInvalid={getIn(touched, 'best_time_to_call') && getIn(errors, 'best_time_to_call')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Form.Control.Feedback type="invalid">
                      {getIn(errors, 'best_time_to_call')}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="d-flex justify-content-end">
                  <Button
                    type="button"
                    onClick={onCancel}
                    variant="outline-primary"
                    size="lg"
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={submitForm}
                    variant="primary"
                    className="ml-3"
                    size="lg"
                    disabled={submitting}
                  >
                    {
                      submitting
                        ? <Spinner animation="border" variant="light" />
                        : <span>Save</span>
                    }
                  </Button>
                </Col>
              </Row>
            </div>
          );
        }
      }
    </Formik>
  );
};
export default EditSellerContact;
